import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Upload, X, CheckCircle2, MessageCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useStore } from "@/context/Store";
import { apiError, http, uid, waLink } from "@/lib/api";
import { Container, Reveal, useSeo } from "@/components/site/common";
import { TermsCheckbox } from "@/pages/LegalPages";

const newItem = (area = "") => ({ _k: uid(), area, quantity: 1, dimensions: "" });

function ItemRow({ item, i, onChange, onRemove, categories, products }) {
  const set = (k) => (e) => onChange({ ...item, [k]: e.target.value });
  return (
    <div className="grid gap-2 rounded-2xl bg-[var(--paper)] p-3 sm:grid-cols-12" data-testid={`idea-item-${i}`}>
      <select required data-testid={`idea-item-area-${i}`} className="field sm:col-span-5" value={item.area} onChange={set("area")}>
        <option value="">Categoria ou produto *</option>
        {categories.map((c) => <optgroup key={c.slug} label={c.title}>{c.subcategories.map((s) => <option key={s.slug} value={`${c.title} – ${s.name}`}>{s.name}</option>)}</optgroup>)}
        <optgroup label="Produtos">{products.map((p) => <option key={p.id} value={`Produto: ${p.name}`}>{p.name}</option>)}</optgroup>
        {item.area && !item.area.includes(" – ") && !item.area.startsWith("Produto: ") && <option value={item.area}>{item.area}</option>}
      </select>
      <input type="number" min={1} data-testid={`idea-item-quantity-${i}`} className="field sm:col-span-2" placeholder="Qtd" value={item.quantity} onChange={set("quantity")} />
      <input data-testid={`idea-item-dimensions-${i}`} className="field sm:col-span-4" placeholder="Medidas (ex.: 120 × 80 cm)" value={item.dimensions} onChange={set("dimensions")} />
      <button type="button" disabled={!onRemove} onClick={onRemove} data-testid={`idea-item-remove-${i}`} className="flex items-center justify-center rounded-xl p-2 hover:text-red-600 disabled:opacity-30 sm:col-span-1" aria-label="Remover item"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function IdeaPage() {
  const [params] = useSearchParams();
  const { categories, products, settings } = useStore();
  const [f, setF] = useState({ name: "", contact: "", email: "", description: "" });
  const [items, setItems] = useState([newItem(params.get("area") || "")]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [accepted, setAccepted] = useState(false);
  useSeo("Ideia personalizada", "Conta-nos a tua ideia: medidas, quantidade e ficheiros de referência. A DUSA responde com orçamento.");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const addFiles = (list) => setFiles((cur) => [...cur, ...Array.from(list)].slice(0, 5));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("items", JSON.stringify(items.map((x) => ({ ...x, quantity: Number(x.quantity) || 1 }))));
    fd.append("accepted_terms", accepted);
    files.forEach((x) => fd.append("files", x));
    try { const { data } = await http.post("/custom-requests", fd); setDone(data.number); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  if (done) return (
    <Container className="py-24 text-center"><div data-testid="idea-success">
      <CheckCircle2 className="mx-auto h-14 w-14 text-[#1faa53]" />
      <h1 className="mt-6 font-display text-5xl font-bold">Ideia recebida!</h1>
      <p className="mt-3 text-[var(--muted-ink)]">Pedido {done}. Respondemos com o orçamento em breve.{f.email && " Enviámos-te uma confirmação por email."}</p>
      <a href={waLink(settings?.whatsapp || "351912861393", `Olá DUSA! Acabei de enviar a ideia ${done} pelo site.`)} target="_blank" rel="noreferrer" className="btn-wa mt-8"><MessageCircle className="h-4 w-4" />Avisar por WhatsApp</a>
      <Link to={`/acompanhar?n=${done}`} className="btn-ghost ml-3 mt-8" data-testid="idea-track-link">Acompanhar pedido</Link>
    </div></Container>
  );

  return (
    <Container className="grid gap-12 py-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <p className="eyebrow">Ideia personalizada</p>
        <h1 className="mt-4 font-display text-5xl font-bold leading-tight tracking-tight sm:text-6xl">Tens uma ideia? <em className="text-gradient">Nós damos-lhe forma.</em></h1>
        <p className="mt-6 max-w-md text-[var(--muted-ink)]">Conta-nos o que precisas — podes juntar vários produtos ou categorias no mesmo pedido — e respondemos com uma proposta à medida.</p>
      </div>
      <form onSubmit={submit} className="space-y-4 rounded-[2rem] border border-[var(--line)] bg-white p-6 sm:p-9 lg:col-span-7" data-testid="idea-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <input required minLength={2} data-testid="idea-name-input" className="field" placeholder="O teu nome *" value={f.name} onChange={set("name")} />
          <input required minLength={6} data-testid="idea-contact-input" className="field" placeholder="Telefone *" value={f.contact} onChange={set("contact")} />
          <input type="email" data-testid="idea-email-input" className="field sm:col-span-2" placeholder="Email (para receberes a confirmação)" value={f.email} onChange={set("email")} />
        </div>
        <p className="pt-2 text-sm font-semibold">O que pretendes</p>
        <div className="space-y-3" data-testid="idea-items-list">
          {items.map((it, i) => <ItemRow key={it._k} i={i} item={it} categories={categories} products={products}
            onChange={(v) => setItems(items.map((x, j) => (j === i ? v : x)))} onRemove={items.length > 1 ? () => setItems(items.filter((_, j) => j !== i)) : null} />)}
        </div>
        {items.length < 20 && <button type="button" data-testid="idea-add-item-button" onClick={() => setItems([...items, newItem()])} className="flex items-center gap-1 text-sm font-semibold text-[var(--violet)]"><Plus className="h-4 w-4" />Adicionar outro produto ou categoria</button>}
        <textarea required minLength={5} maxLength={1500} data-testid="idea-description-input" className="field min-h-32" placeholder="Descreve a tua ideia: cores, textos, prazos… *" value={f.description} onChange={set("description")} />
        <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }} className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[var(--line)] bg-[var(--paper)] p-6 text-center text-sm transition-colors hover:border-[var(--violet)]">
          <Upload className="h-6 w-6 text-[var(--violet)]" /><span className="font-semibold">Arrasta ou escolhe ficheiros</span><span className="text-xs text-[var(--muted-ink)]">Imagens ou PDF · até 5 ficheiros · 10 MB cada</span>
          <input data-testid="idea-files-input" type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </label>
        {files.length > 0 && <ul className="space-y-2" data-testid="idea-files-list">{files.map((x, i) => <li key={`${x.name}-${x.size}-${x.lastModified}`} className="flex items-center justify-between rounded-xl bg-[var(--lilac)] px-4 py-2 text-sm"><span className="truncate">{x.name}</span><button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remover"><X className="h-4 w-4" /></button></li>)}</ul>}
        <TermsCheckbox testid="idea-terms-checkbox" checked={accepted} onChange={setAccepted} />
        <button data-testid="idea-submit-button" disabled={busy} className="btn-primary w-full">{busy ? "A enviar…" : "Enviar a minha ideia"}</button>
      </form>
    </Container>
  );
}

export function AboutPage() {
  const { settings } = useStore();
  useSeo("Sobre a DUSA", "Conhece a DUSA: criações personalizadas em vinil, murais, acrílico e têxteis.");
  const paragraphs = useMemo(() => (settings?.about || "").split("\n").filter(Boolean), [settings?.about]);
  return (
    <Container className="py-16">
      <div className="grid gap-12 lg:grid-cols-12" data-testid="about-page">
        <Reveal className="lg:col-span-5"><p className="eyebrow">Sobre nós</p><h1 className="mt-4 font-display text-5xl font-bold tracking-tight sm:text-6xl">Olá, somos a <em className="text-gradient">DUSA.</em></h1><img src="/logo.jpg" alt="Logótipo DUSA" className="mt-10 w-48 rounded-3xl shadow-xl" /></Reveal>
        <Reveal delay={0.1} className="space-y-5 text-lg leading-relaxed text-[var(--muted-ink)] lg:col-span-7 lg:pt-16">{paragraphs.map((t, i) => <p key={`${i}-${t.slice(0, 20)}`}>{t}</p>)}</Reveal>
      </div>
    </Container>
  );
}

export function FaqPage() {
  const { settings } = useStore();
  useSeo("Perguntas frequentes", "Prazos, envios, materiais e como pedir orçamento à DUSA.");
  return (
    <Container className="max-w-3xl py-16">
      <div data-testid="faq-page">
        <p className="eyebrow">Ajuda</p>
        <h1 className="mt-4 font-display text-5xl font-bold tracking-tight">Perguntas frequentes</h1>
        <Accordion type="single" collapsible className="mt-10">
          {(settings?.faq || []).map((x, i) => (
            <AccordionItem key={`${i}-${x.q}`} value={`f${i}`} className="border-[var(--line)]">
              <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left text-base font-semibold hover:text-[var(--violet)] hover:no-underline">{x.q}</AccordionTrigger>
              <AccordionContent className="text-[var(--muted-ink)]">{x.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Container>
  );
}
